import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { workbackTemplateAPI, taxonomyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../components/ui/alert-dialog';
import { Plus, Save, CalendarClock, Pencil, Trash2, Star, X, ListChecks, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';

const emptyPhases = (base) => base.map(p => ({
  phase_number: p.phase_number, phase_name: p.phase_name, value: p.offset_days ?? 0, unit: 'days',
  is_go_live: !!p.is_go_live, activities: [...(p.activities || [])],
}));

const blankPhase = (nextNumber) => ({
  phase_number: nextNumber, phase_name: '', value: 0, unit: 'days', is_go_live: false, activities: [],
});

export const WorkbackTemplateSettingsPage = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assetTypeOptions, setAssetTypeOptions] = useState([]);
  const [readinessOptions, setReadinessOptions] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', asset_type: '', readiness_stage: '', phases: [] });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activityDrafts, setActivityDrafts] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await workbackTemplateAPI.list();
      setTemplates(data);
    } catch { toast.error('Failed to load workback templates'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    taxonomyAPI.getCategory('asset_type').then(c => setAssetTypeOptions(c.items.filter(i => i.active))).catch(() => {});
    taxonomyAPI.getCategory('readiness_stage').then(c => setReadinessOptions(c.items.filter(i => i.active))).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setActivityDrafts({});
    setForm({ title: '', asset_type: '', readiness_stage: '', phases: [{ ...blankPhase(1), is_go_live: true, phase_name: 'Go Live' }] });
    setEditorOpen(true);
  };

  const openEdit = (tpl) => {
    setEditingId(tpl.id);
    setActivityDrafts({});
    setForm({ title: tpl.title, asset_type: tpl.asset_type || '', readiness_stage: tpl.readiness_stage || '', phases: emptyPhases(tpl.phases) });
    setEditorOpen(true);
  };

  const setPhaseValue = (idx, value) => setForm(f => ({ ...f, phases: f.phases.map((p, i) => i === idx ? { ...p, value: value === '' ? 0 : parseInt(value, 10) } : p) }));
  const setPhaseUnit = (idx, unit) => setForm(f => ({
    ...f, phases: f.phases.map((p, i) => {
      if (i !== idx) return p;
      const days = p.unit === 'weeks' ? p.value * 5 : p.value;
      return { ...p, unit, value: unit === 'weeks' ? Math.round(days / 5) : days };
    })
  }));
  const setPhaseName = (idx, name) => setForm(f => ({ ...f, phases: f.phases.map((p, i) => i === idx ? { ...p, phase_name: name } : p) }));
  const setGoLive = (idx) => setForm(f => ({ ...f, phases: f.phases.map((p, i) => ({ ...p, is_go_live: i === idx })) }));

  const addPhase = () => setForm(f => {
    const nextNumber = (f.phases.reduce((max, p) => Math.max(max, p.phase_number), 0)) + 1;
    return { ...f, phases: [...f.phases, blankPhase(nextNumber)] };
  });
  const removePhase = (idx) => setForm(f => {
    if (f.phases.length <= 1) { toast.error('A template needs at least one phase'); return f; }
    const removedGoLive = f.phases[idx].is_go_live;
    const remaining = f.phases.filter((_, i) => i !== idx);
    if (removedGoLive && remaining.length) remaining[remaining.length - 1] = { ...remaining[remaining.length - 1], is_go_live: true };
    return { ...f, phases: remaining };
  });

  const addActivity = (idx) => {
    const text = (activityDrafts[idx] || '').trim();
    if (!text) return;
    setForm(f => ({ ...f, phases: f.phases.map((p, i) => i === idx ? { ...p, activities: [...p.activities, text] } : p) }));
    setActivityDrafts(d => ({ ...d, [idx]: '' }));
  };
  const removeActivity = (idx, activityIdx) => setForm(f => ({
    ...f, phases: f.phases.map((p, i) => i === idx ? { ...p, activities: p.activities.filter((_, ai) => ai !== activityIdx) } : p)
  }));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Please enter a template title'); return; }
    if (form.phases.some(p => !p.phase_name.trim())) { toast.error('Every phase needs a name'); return; }
    if (!form.phases.some(p => p.is_go_live)) { toast.error('Mark exactly one phase as the Go-Live milestone'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        asset_type: form.asset_type || null,
        readiness_stage: form.readiness_stage || null,
        phases: form.phases.map(p => ({
          phase_number: p.phase_number, phase_name: p.phase_name.trim(),
          offset_days: p.unit === 'weeks' ? p.value * 5 : p.value,
          is_go_live: p.is_go_live, activities: p.activities,
        })),
        updated_by: user?.name || user?.email,
      };
      if (editingId) {
        await workbackTemplateAPI.updateTemplate(editingId, payload);
        toast.success('Template updated');
      } else {
        await workbackTemplateAPI.create(payload);
        toast.success('Template created');
      }
      setEditorOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await workbackTemplateAPI.remove(deleteTarget.id);
      toast.success('Template deleted');
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete template');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="workback-template-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold">Workback Templates</h1>
          <p className="text-muted-foreground">
            Build named workback templates (in working days) per Asset Type / Readiness Stage. Linked to a
            Content Item, they auto-calculate its projected Go-Live Date from the PO Receipt Date.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="new-workback-template-btn">
          <Plus className="w-4 h-4 mr-2" />New Template
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {templates.map(tpl => (
            <Card key={tpl.id} data-testid={`workback-template-card-${tpl.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    {tpl.is_default && <Star className="w-4 h-4 text-amber-500 fill-amber-500" />}
                    {tpl.title}
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(tpl)} className="text-muted-foreground hover:text-foreground p-1" data-testid={`edit-workback-template-${tpl.id}`}><Pencil className="w-4 h-4" /></button>
                    {!tpl.is_default && (
                      <button onClick={() => setDeleteTarget(tpl)} className="text-muted-foreground hover:text-red-600 p-1" data-testid={`delete-workback-template-${tpl.id}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap mt-1">
                  {tpl.is_default && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Default</Badge>}
                  {tpl.asset_type && <Badge variant="outline" className="text-[10px]">{tpl.asset_type}</Badge>}
                  {tpl.readiness_stage && <Badge variant="outline" className="text-[10px]">{readinessOptions.find(r => r.value === tpl.readiness_stage)?.label || tpl.readiness_stage}</Badge>}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-1.5">
                  {tpl.phases.map(p => (
                    <div key={p.phase_number} className="flex items-center justify-between text-xs py-1 border-b border-border/50 last:border-0">
                      <span className="text-muted-foreground flex items-center gap-1">
                        {p.phase_number}. {p.phase_name}
                        {p.is_go_live && <Star className="w-3 h-3 text-amber-500 fill-amber-500" data-testid={`go-live-star-${tpl.id}-${p.phase_number}`} />}
                      </span>
                      <span className="font-medium">{p.offset_days} working day{p.offset_days !== 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="workback-template-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Workback Template' : 'New Workback Template'}</DialogTitle>
            <DialogDescription>Build fully custom phases — add, remove, rename, set working-day offsets, checklist activities, and mark one phase as the Go-Live milestone.</DialogDescription>
          </DialogHeader>
          <Link
            to="/taxonomy?category=content_activity_status"
            className="text-xs text-lime-700 hover:underline flex items-center gap-1 -mt-2"
            data-testid="manage-activity-status-values-link"
          >
            Manage available Status values (Pending/In Progress/Completed/N/A) in Taxonomy Settings <ArrowUpRight className="w-3 h-3" />
          </Link>
          <div className="space-y-4">
            <div>
              <Label>Template Title</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Microskilling Fast Track" data-testid="workback-template-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Asset Type (optional tag)</Label>
                <Select value={form.asset_type} onValueChange={v => setForm({ ...form, asset_type: v })}>
                  <SelectTrigger data-testid="workback-template-asset-type-select"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>{assetTypeOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Readiness Stage (optional tag)</Label>
                <Select value={form.readiness_stage} onValueChange={v => setForm({ ...form, readiness_stage: v })}>
                  <SelectTrigger data-testid="workback-template-readiness-select"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>{readinessOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2"><CalendarClock className="w-4 h-4" />Phases</Label>
                <Button type="button" variant="outline" size="sm" onClick={addPhase} data-testid="add-workback-phase-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" />Add Phase
                </Button>
              </div>
              <div className="space-y-3">
                {form.phases.map((p, idx) => (
                  <div key={idx} className="border rounded-lg p-3 space-y-2.5" data-testid={`workback-template-phase-row-${idx}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-lime/20 text-lime-foreground text-[11px] flex items-center justify-center font-bold flex-shrink-0">{p.phase_number}</span>
                      <Input
                        value={p.phase_name}
                        onChange={e => setPhaseName(idx, e.target.value)}
                        placeholder="Phase name"
                        className="flex-1"
                        data-testid={`workback-template-phase-name-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => setGoLive(idx)}
                        title={p.is_go_live ? 'Go-Live milestone' : 'Mark as Go-Live milestone'}
                        className="flex-shrink-0 p-1.5 rounded-full hover:bg-amber-50"
                        data-testid={`set-go-live-phase-${idx}`}
                      >
                        <Star className={`w-4 h-4 ${p.is_go_live ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground'}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removePhase(idx)}
                        className="flex-shrink-0 p-1.5 rounded-full hover:bg-red-50 text-muted-foreground hover:text-red-600"
                        data-testid={`remove-workback-phase-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-3 pl-8">
                      <span className="text-xs text-muted-foreground flex-shrink-0">Offset from PO Receipt:</span>
                      <Input type="number" min="0" className="w-20" value={p.value} onChange={e => setPhaseValue(idx, e.target.value)} data-testid={`workback-template-phase-value-${idx}`} />
                      <Select value={p.unit} onValueChange={v => setPhaseUnit(idx, v)}>
                        <SelectTrigger className="w-24" data-testid={`workback-template-phase-unit-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="days">days</SelectItem><SelectItem value="weeks">weeks</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="pl-8 space-y-1.5">
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><ListChecks className="w-3 h-3" />Checklist Activities (optional)</span>
                      {p.activities.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {p.activities.map((a, ai) => (
                            <span key={ai} className="flex items-center gap-1 text-xs bg-muted rounded-full pl-2.5 pr-1.5 py-1" data-testid={`phase-activity-chip-${idx}-${ai}`}>
                              {a}
                              <button type="button" onClick={() => removeActivity(idx, ai)} data-testid={`remove-phase-activity-${idx}-${ai}`}>
                                <X className="w-3 h-3 text-muted-foreground hover:text-red-600" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Input
                          value={activityDrafts[idx] || ''}
                          onChange={e => setActivityDrafts(d => ({ ...d, [idx]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addActivity(idx); } }}
                          placeholder="Add a checklist activity and press Enter"
                          className="h-8 text-sm"
                          data-testid={`workback-template-activity-input-${idx}`}
                        />
                        <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => addActivity(idx)} data-testid={`add-phase-activity-btn-${idx}`}>Add</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="save-workback-template-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>Content items already linked to this template keep their calculated dates, but it can no longer be selected for new ones.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-workback-template-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WorkbackTemplateSettingsPage;
